<div class="probability-experiment">
  <!-- Title + BINS intro -->
  <h3 class="experiment-title">Setting up a Binomial Experiment</h3>
  <p class="experiment-intro">
    Not every random experiment is binomial. For it to qualify, four conditions must hold. We call them
    <strong>BINS</strong>:
  </p>
  <ul class="bins-list">
    <li><strong class="bins-letter">B</strong>inary outcomes: each trial results in success or failure</li>
    <li><strong class="bins-letter">I</strong>ndependent trials: the outcome of one trial doesn't affect another</li>
    <li>Fixed <strong class="bins-letter">N</strong>umber of trials, decided in advance</li>
    <li><strong class="bins-letter">S</strong>ame probability of success on each trial</li>
  </ul>
  <p class="experiment-intro">As you set up the experiment below, you'll see each one fall into place.</p>

  <!-- Experiment Selector -->
  <mat-tab-group
    class="custom-mat-tab size-s experiment-tabs"
    mat-stretch-tabs="false"
    mat-align-tabs="start"
    [selectedIndex]="selectedTabIndex()"
    (selectedIndexChange)="onTabChange($event)"
  >
    @for (exp of experiments; track exp.type) {
      <mat-tab>
        <ng-template mat-tab-label>
          <dojo-outcome-svg class="experiment-tabs__icon" [svgType]="exp.tabSvgType" />
          {{ exp.name }}
        </ng-template>
      </mat-tab>
    }
  </mat-tab-group>

  <!-- Description -->
  <div class="experiment-description" [innerHTML]="setupNarrativeHtml()"></div>

  <!-- Table -->
  <table class="experiment-table">
    <!-- Sample Space header -->
    <tr>
      <th class="experiment-table__corner"></th>
      <th class="experiment-table__group-header" [attr.colspan]="selectedExperiment().outcomes.length">
        Sample Space <span [innerHTML]="uHtml"></span>
        <dojo-icon
          icon="circle-info"
          [size]="11"
          [matTooltip]="selectedExperiment().sampleSpaceDescription"
          matTooltipPosition="right"
          class="experiment-table__info-icon"
        />
      </th>
    </tr>

    <!-- X row with SVGs -->
    <tr>
      <th class="experiment-table__row-header">
        <span [innerHTML]="xHtml"></span>
      </th>
      @for (outcome of selectedExperiment().outcomes; track outcome.label; let i = $index) {
        <td class="experiment-table__outcome">
          <div class="experiment-table__svg">
            <dojo-outcome-svg [svgType]="outcome.svgType" />
          </div>
          <span class="experiment-table__label">{{ outcome.label }}</span>
          <span class="experiment-table__value-tag" [innerHTML]="valueTagHtmls()[i]"></span>
        </td>
      }
    </tr>

    <!-- Distribution header -->
    <tr>
      <th class="experiment-table__corner"></th>
      <th class="experiment-table__group-header" [attr.colspan]="selectedExperiment().outcomes.length">
        Distribution
        @if (isUniform()) {
          (Discrete Uniform)
          <dojo-icon
            icon="circle-info"
            [size]="11"
            [matTooltip]="distributionTooltip"
            matTooltipPosition="right"
            class="experiment-table__info-icon"
          />
        }
      </th>
    </tr>

    <!-- P(X) row with fractions -->
    <tr>
      <th class="experiment-table__row-header">
        <span [innerHTML]="pxHtml"></span>
      </th>
      @for (outcome of selectedExperiment().outcomes; track outcome.label; let i = $index) {
        <td
          class="experiment-table__value"
          [class.experiment-table__value--selected]="targetOutcomes()[i]"
          [innerHTML]="fractionLabels()[i]"
        ></td>
      }
    </tr>

    <!-- Target Outcome header -->
    <tr>
      <th class="experiment-table__corner"></th>
      <th
        class="experiment-table__group-header experiment-table__group-header--action"
        [attr.colspan]="selectedExperiment().outcomes.length"
      >
        Define Success
        <span class="experiment-table__group-hint">— tick the outcomes that count as a "success"</span>
      </th>
    </tr>

    <!-- Checkbox row -->
    <tr>
      <th class="experiment-table__row-header">
        <span [innerHTML]="aHtml"></span>
      </th>
      @for (outcome of selectedExperiment().outcomes; track outcome.label; let i = $index) {
        <td class="experiment-table__checkbox">
          <mat-checkbox color="primary" [checked]="targetOutcomes()[i]" (change)="toggleTargetOutcome(i)" />
        </td>
      }
    </tr>

    <!-- Probability of success row -->
    <tr>
      <td class="experiment-table__success-corner"></td>
      <td class="experiment-table__success-row" [attr.colspan]="selectedExperiment().outcomes.length">
        <span class="experiment-table__success-desc">{{ successDescription() }}</span>
        <br />
        <span class="experiment-table__success-value" [innerHTML]="successProbabilityHtml()"></span>
      </td>
    </tr>
  </table>

  @if (successProbability() > 0 && successProbability() < 1) {
    <!-- Full-width intro, equation, and BINS conditions -->
    <div class="binomial-narrative-card binomial-narrative-card--full">
      <p class="binomial-narrative-card__text">
        If we <strong>{{ binomialVerb() }}</strong>
        <span
          class="binomial-narrative-card__n-wrapper"
          [matTooltip]="trialsLockedTooltip"
          [matTooltipDisabled]="!isTrialsLocked()"
        >
          <span [innerHTML]="nColoredHtml"></span> =
          <input
            type="number"
            class="binomial-narrative-card__n-input"
            [value]="trials()"
            [min]="1"
            [max]="200"
            [disabled]="isTrialsLocked()"
            (change)="onInlineTrialsChange($event)"
            (keydown.enter)="$any($event.target).blur()"
          />
          <span class="n-stepper-group">
            <button class="n-stepper-btn" [disabled]="isTrialsLocked() || trials() <= 1" (click)="stepTrials(-1)">
              <dojo-icon icon="minus" [size]="8" />
            </button>
            <button class="n-stepper-btn" [disabled]="isTrialsLocked() || trials() >= 200" (click)="stepTrials(1)">
              <dojo-icon icon="plus" [size]="8" />
            </button>
          </span>
        </span>
        times, where each trial has a probability of success
        <span [innerHTML]="pALatexHtml()"></span>, i.e.
        <span class="binomial-narrative-card__outcome-icons">
          @for (svg of successSvgTypes(); track svg) {
            <dojo-outcome-svg [svgType]="svg" />
          }
        </span>
        @if (successEventDescription()) {
          ({{ successEventDescription() }}),
        }
        as defined by you by ticking the checkmarks above, then the number of successes
        <span [innerHTML]="xHtml"></span> follows a <strong>Binomial distribution</strong>:
      </p>
      <div class="binomial-narrative-card__equation" [innerHTML]="binomialDistEqHtml()"></div>
    </div>

    <!-- Two-column: BINS + PMF text + chart -->
    <div class="binomial-split-layout">
      <div class="binomial-split-layout__text">
        <div class="binomial-narrative-card">
          <div class="bins-list-section" [innerHTML]="binsListHtml()"></div>
          <p class="binomial-narrative-card__text">
            The probability of getting exactly <span [innerHTML]="kLabelHtml"></span> successes out of
            <span [innerHTML]="nColoredHtml"></span> trials is given by:
          </p>
          <div class="binomial-narrative-card__equation" [innerHTML]="binomialPmfEqHtml()"></div>
          <p class="binomial-narrative-card__text">
            Over <span class="binomial-narrative-card__n-text">{{ trials() }}</span> trials, we'd expect
            <span [innerHTML]="binomialExpectedMuHtml()"></span>
            successes on average, with results typically spread within
            <span [innerHTML]="binomialExpectedSigmaHtml()"></span>
            of that value. The chart visualizes this. Each bar represents <span [innerHTML]="pxkLabelHtml"></span>, the
            dashed curve is the normal approximation, and the vertical <span [innerHTML]="muLabelHtml"></span> line
            marks the expected value.
          </p>
        </div>
      </div>
      <div class="binomial-split-layout__chart">
        <div class="binomial-chart">
          <dojo-echarts-chart [options]="binomialChartOptions()" />
          <span class="binomial-chart__x-label" [innerHTML]="kLabelHtml"></span>
          <span class="binomial-chart__y-label" [innerHTML]="pxkLabelHtml"></span>
        </div>
      </div>
    </div>

    <!-- Run the Experiment card -->
    <div class="run-experiment-card">
      <h4 class="experiment-section-title">Run Experiments</h4>
      <p class="run-experiment-card__text" [innerHTML]="runExperimentNarrativeHtml()"></p>
      <div class="run-experiment-card__controls">
        <label
          class="run-experiment-card__slider-label"
          [matTooltip]="trialsLockedTooltip"
          [matTooltipDisabled]="!isTrialsLocked()"
        >
          <span [innerHTML]="nColoredHtml"></span> =
          <input
            type="number"
            class="binomial-narrative-card__n-input"
            [value]="trials()"
            [min]="1"
            [max]="200"
            [disabled]="isTrialsLocked()"
            (change)="onInlineTrialsChange($event)"
            (keydown.enter)="$any($event.target).blur()"
          />
          <span class="n-stepper-group">
            <button class="n-stepper-btn" [disabled]="isTrialsLocked() || trials() <= 1" (click)="stepTrials(-1)">
              <dojo-icon icon="minus" [size]="9" />
            </button>
            <button class="n-stepper-btn" [disabled]="isTrialsLocked() || trials() >= 200" (click)="stepTrials(1)">
              <dojo-icon icon="plus" [size]="9" />
            </button>
          </span>
        </label>
        <dojo-button size="xs" customColor="sky" [disabled]="maxRunsReached()" (click)="runExperiment()">
          <dojo-icon icon="play" class="mr-2" />
          <span>Run Single Experiment</span>
        </dojo-button>
        @if (!maxRunsReached()) {
          <dojo-button size="xs" customColor="sky" (click)="runBatchExperiments(20)">
            <dojo-icon icon="forward" class="mr-2" />
            <span>Run 20 Experiments</span>
          </dojo-button>
        }
        @if (visibleResults().length > 0) {
          <dojo-button size="xs" fill="outline" (click)="resetResults()">
            <dojo-icon icon="rotate-left" class="mr-2" />
            <span>Reset</span>
          </dojo-button>
        }
      </div>
    </div>

    @if (visibleResults().length > 0) {
      <div class="experiment-runs-layout">
        <div class="experiment-runs-layout__accordion" #runsScrollContainer>
          <dojo-experiment-run-log
            [runs]="runs()"
            [experiment]="selectedExperiment()"
            [targetOutcomes]="targetOutcomes()"
            [successProbability]="successProbability()"
            (runHover)="hoveredK.set($event)"
          />
        </div>
        <div class="experiment-runs-layout__sidebar" #chartsColumn>
          <div class="binomial-chart binomial-chart--sidebar">
            <span class="binomial-chart__title">Empirical vs. Theoretical Distribution</span>
            <dojo-echarts-chart
              [options]="empiricalDistributionChartOptions()"
              (chartInit)="onEmpiricalChartInit($event)"
            />
            <span class="binomial-chart__x-label" [innerHTML]="kLabelHtml"></span>
            <span class="binomial-chart__y-label">Frequency</span>
          </div>
          <div
            class="binomial-narrative-card binomial-narrative-card--post-experiment"
            [innerHTML]="postExperimentNarrativeHtml()"
          ></div>
        </div>
      </div>
    }
  }
</div>
